import React from 'react';
import Icon from './Icon';
import '../../styles/common/theme.css';
import '../../styles/common/icons.css';
import '../../styles/common/actionButtons.css';

export interface ActionConfig {
  type: 'view' | 'edit' | 'delete' | 'copy' | 'save' | 'submit' | 'test' | 'cancel';
  variant?: 'primary' | 'secondary' | 'danger' | 'default';
  label?: string; // Custom label override
  icon?: string; // Custom icon override
  show?: boolean;
  disabled?: boolean;
  onClick?: (id: number) => void;
}

export interface ActionButtonsProps {
  item: {
    id: number;
  };
  onView?: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onCopy?: (id: number) => void;
  showView?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  showCopy?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  // New props for unified component
  mode?: 'icon' | 'labeled' | 'mixed';
  actions?: ActionConfig[];
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  item,
  onView,
  onEdit,
  onDelete,
  onCopy,
  showView = true,
  showEdit = true,
  showDelete = true,
  showCopy = false,
  canEdit = true,
  canDelete = true,
  size = 'sm',
  className = '',
  mode = 'icon',
  actions
}) => {
  // Default action configurations
  const defaultActions: ActionConfig[] = [
    {
      type: 'view',
      variant: 'primary',
      label: 'View',
      icon: 'arrow_outward',
      show: showView,
      disabled: false,
      onClick: onView
    },
    {
      type: 'edit',
      variant: 'secondary',
      label: 'Edit',
      icon: 'edit',
      show: showEdit,
      disabled: !canEdit,
      onClick: onEdit
    },
    {
      type: 'copy',
      variant: 'secondary',
      label: 'Copy',
      icon: 'content_copy',
      show: showCopy,
      disabled: false,
      onClick: onCopy
    },
    {
      type: 'delete',
      variant: 'danger',
      label: 'Delete',
      icon: 'delete',
      show: showDelete,
      disabled: !canDelete,
      onClick: onDelete
    }
  ];

  // Use provided actions or default actions
  const actionConfigs = actions || defaultActions;

  const handleAction = (e: React.MouseEvent, action: ActionConfig) => {
    e.stopPropagation();
    action.onClick?.(item.id);
  };

  const getButtonClasses = (action: ActionConfig, isLabeled: boolean) => {
    const baseClasses = ['action-button'];
    
    if (isLabeled) {
      baseClasses.push('action-button-labeled');
      baseClasses.push(`action-button-${action.variant || 'default'}`);
      baseClasses.push(`action-button-${size}`);
    } else {
      baseClasses.push('action-button-icon');
    }
    
    if (action.disabled) {
      baseClasses.push('action-button-disabled');
    }
    
    if (className) {
      baseClasses.push(className);
    }
    
    return baseClasses.filter(Boolean).join(' ');
  };

  const renderActionButton = (action: ActionConfig) => {
    if (!action.show) return null;

    const isLabeled = mode === 'labeled' || (mode === 'mixed' && Boolean(action.label));
    const buttonClasses = getButtonClasses(action, isLabeled);

    if (isLabeled) {
      return (
        <button
          key={action.type}
          className={buttonClasses}
          onClick={(e) => handleAction(e, action)}
          disabled={Boolean(action.disabled)}
          title={action.disabled ? `Cannot ${action.type}` : (action.label || action.type)}
          type="button"
        >
          {action.icon && (
            <span className="material-symbols-outlined action-icon">{action.icon}</span>
          )}
          <span className="action-label">{action.label}</span>
        </button>
      );
    } else {
      return (
        <Icon
          key={action.type}
          name={action.icon || 'help'}
          variant={action.variant === 'danger' ? 'delete' : action.variant === 'secondary' ? 'edit' : undefined}
          size={size}
          onClick={(e) => handleAction(e, action)}
          disabled={Boolean(action.disabled)}
          title={action.disabled ? `Cannot ${action.type}` : action.label}
          className={className}
        />
      );
    }
  };

  return (
    <div className={`action-buttons-container ${mode === 'labeled' ? 'action-buttons-labeled' : 'action-buttons-icon'}`}>
      {actionConfigs.map(renderActionButton)}
    </div>
  );
};

export default ActionButtons; 